import {useEffect, useCallback, useState, useRef} from 'react'
import {useAuth} from '../app/modules/auth'
import {pushNotificationService} from '../services/pushNotificationApi'
import {tokenLogger, flushLogs} from '../Utils/ccpLogger'
import {toast} from 'react-toastify'

export interface PushNotificationState {
  isRegistered: boolean
  isLoading: boolean
  error: string | null
  deviceToken: string | null
}

const isNotificationApiAvailable = (): boolean =>
  typeof window !== 'undefined' && typeof window.Notification !== 'undefined'

const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (!isNotificationApiAvailable()) {
    return 'unsupported'
  }
  return window.Notification.permission
}

export const usePushNotifications = () => {
  const {auth, currentUser} = useAuth()
  const [state, setState] = useState<PushNotificationState>({
    isRegistered: false,
    isLoading: false,
    error: null,
    deviceToken: null,
  })

  const [lastRegistrationAttempt, setLastRegistrationAttempt] = useState<number>(0)
  const [isRegistering, setIsRegistering] = useState<boolean>(false)

  const registerDevice = useCallback(async () => {
    const now = Date.now()
    if (now - lastRegistrationAttempt < 3000) {
      return false
    }

    if (isRegistering) {
      return false
    }

    if (!auth?.isAuthenticated || !currentUser) {
      return false
    }

    setIsRegistering(true)
    setLastRegistrationAttempt(now)
    setState((prev) => ({...prev, isLoading: true, error: null}))

    try {
      const success = await pushNotificationService.initializeAndRegister()

      if (success) {
        const deviceToken = pushNotificationService.getCurrentDeviceToken()
        setState((prev) => ({
          ...prev,
          isRegistered: true,
          isLoading: false,
          deviceToken,
        }))

        // toast.success('Push notifications enabled successfully')
        setIsRegistering(false)
        return true
      } else {
        setState((prev) => ({
          ...prev,
          isRegistered: false,
          isLoading: false,
          error: 'Failed to register for push notifications',
        }))

        // Don't show error toast for permission denied, as it's a user choice
        // if (Notification.permission !== 'denied') {
        //   toast.warning('Failed to enable push notifications. Retrying automatically...')
        // }
        setIsRegistering(false)
        return false
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      setState((prev) => ({
        ...prev,
        isRegistered: false,
        isLoading: false,
        error: errorMessage,
      }))

      toast.error('Error setting up push notifications. Retrying automatically...')
      setIsRegistering(false)
      return false
    }
  }, [auth?.isAuthenticated, currentUser, lastRegistrationAttempt, isRegistering]) // Removed state.isRegistered dependency

  // Unregister device from push notifications
  const unregisterDevice = useCallback(async () => {
    setState((prev) => ({...prev, isLoading: true, error: null}))

    try {
      const success = await pushNotificationService.unregisterDevice()

      // Reset session state
      pushNotificationService.resetSession()

      setState((prev) => ({
        ...prev,
        isRegistered: false,
        isLoading: false,
        deviceToken: null,
      }))

      if (success) {
      }

      return success
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))

      return false
    }
  }, [])

  // Refresh registration (useful when token changes)
  const refreshRegistration = useCallback(async () => {
    if (!auth?.isAuthenticated || !currentUser) {
      return false
    }

    setState((prev) => ({...prev, isLoading: true, error: null}))

    try {
      const success = await pushNotificationService.refreshRegistration()

      if (success) {
        const deviceToken = pushNotificationService.getCurrentDeviceToken()
        setState((prev) => ({
          ...prev,
          isRegistered: true,
          isLoading: false,
          deviceToken,
        }))
      } else {
        // Keep the LastUsedAt poll alive whenever a cached token can still heartbeat.
        // Clearing isRegistered here used to stop the only keep-alive and re-open the
        // Force Update Token modal after the 20-minute freshness window.
        const cachedToken = pushNotificationService.getCurrentDeviceToken()
        setState((prev) => ({
          ...prev,
          isRegistered: Boolean(cachedToken),
          isLoading: false,
          error: cachedToken
            ? 'Push token refresh failed; continuing heartbeat with cached token'
            : 'Failed to refresh push notification registration',
          deviceToken: cachedToken,
        }))
      }

      return success
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      const cachedToken = pushNotificationService.getCurrentDeviceToken()
      setState((prev) => ({
        ...prev,
        isRegistered: Boolean(cachedToken),
        isLoading: false,
        error: errorMessage,
        deviceToken: cachedToken,
      }))

      return false
    }
  }, [auth?.isAuthenticated, currentUser])

  const checkPermission = useCallback(() => {
    return getNotificationPermission()
  }, [])

  const requestPermission = useCallback(async () => {
    try {
      if (!isNotificationApiAvailable()) {
        return 'denied'
      }

      const permission = await window.Notification.requestPermission()

      if (permission === 'granted') {
        if (auth?.isAuthenticated && currentUser && !isRegistering) {
          await registerDevice()
        }
      }

      return permission
    } catch (error) {
      return 'denied'
    }
  }, [auth?.isAuthenticated, currentUser, isRegistering, registerDevice])

  useEffect(() => {
    const handleAuthChange = async () => {
      try {
        if (auth?.isAuthenticated && currentUser) {
          const permission = getNotificationPermission()
          if (permission === 'granted') {
            if (!isRegistering && !pushNotificationService.isDeviceRegistered()) {
              await registerDevice()
            }
          } else if (permission === 'default') {
            await requestPermission()
          }
        } else {
          if (state.isRegistered) {
            await unregisterDevice()
          } else {
            pushNotificationService.resetSession()
          }
        }
      } catch {
        return
      }
    }

    handleAuthChange()
  }, [auth?.isAuthenticated, currentUser?.result?.code, isRegistering])

  const retryAttemptRef = useRef<number>(0)
  useEffect(() => {
    if (!auth?.isAuthenticated || !currentUser || state.isRegistered) {
      retryAttemptRef.current = 0
      return
    }

    if (getNotificationPermission() !== 'granted' || isRegistering) {
      return
    }

    const attempt = retryAttemptRef.current
    const delayMs = Math.min(30_000 * 2 ** attempt, 5 * 60 * 1000)

    const timeoutId = setTimeout(() => {
      retryAttemptRef.current = attempt + 1
      registerDevice()
    }, delayMs)

    return () => clearTimeout(timeoutId)
  }, [auth?.isAuthenticated, currentUser, state.isRegistered, isRegistering, registerDevice])

  useEffect(() => {
    const initializeState = () => {
      const deviceToken = pushNotificationService.getCurrentDeviceToken()
      const isRegistered = pushNotificationService.isDeviceRegistered()

      setState((prev) => ({
        ...prev,
        isRegistered,
        deviceToken,
      }))
    }

    initializeState()
  }, [])

  useEffect(() => {
    const onPermissionGranted = () => {
      if (auth?.isAuthenticated && currentUser && getNotificationPermission() === 'granted') {
        registerDevice()
      }
    }
    window.addEventListener('aac-notification-permission-granted', onPermissionGranted)
    return () => window.removeEventListener('aac-notification-permission-granted', onPermissionGranted)
  }, [auth?.isAuthenticated, currentUser, registerDevice])

  // Force Update Token runs on the service singleton (TokenUpdateModal). Sync React
  // isRegistered so this hook's poll effect starts; service.isRegistered alone does not.
  useEffect(() => {
    const onForceUpdated = () => {
      const deviceToken = pushNotificationService.getCurrentDeviceToken()
      if (!deviceToken) return
      setState((prev) => ({
        ...prev,
        isRegistered: true,
        isLoading: false,
        error: null,
        deviceToken,
      }))
    }
    window.addEventListener('aac-device-token-force-updated', onForceUpdated)
    return () => window.removeEventListener('aac-device-token-force-updated', onForceUpdated)
  }, [])

  // Effect to handle token refresh periodically (optional)
  useEffect(() => {
    if (!auth?.isAuthenticated || !state.isRegistered) {
      return
    }

    // Set up periodic token refresh (every 24 hours)
    const refreshInterval = setInterval(() => {
      refreshRegistration()
    }, 24 * 60 * 60 * 1000) // 24 hours

    return () => clearInterval(refreshInterval)
  }, [auth?.isAuthenticated, state.isRegistered, refreshRegistration])

  // Effect to log when the tab is being closed/hidden. Closing the tab tears down
  // the poll interval above, which is the ONLY thing refreshing LastUsedAt — so a
  // tab close is the most likely moment the heartbeat silently dies. Log it (with
  // agentEmail) so we can correlate stale device tokens with tab-close events.
  useEffect(() => {
    if (!auth?.isAuthenticated || !currentUser) {
      return
    }

    const agentEmail = currentUser?.result?.email ?? null

    const logTabClose = (reason: 'pagehide' | 'hidden') => {
      tokenLogger.warn('🔔 Tab closing/hidden — device-token poll heartbeat stopping', {
        agentEmail,
        reason,
        isRegistered: pushNotificationService.isDeviceRegistered(),
        permission: getNotificationPermission(),
        visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
      })
      flushLogs()
    }

    const handlePageHide = () => logTabClose('pagehide')
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        logTabClose('hidden')
        return
      }
      // Catch up LastUsedAt after background-tab timer throttle before the 20-min window expires.
      if (
        document.visibilityState === 'visible' &&
        pushNotificationService.getCurrentDeviceToken()
      ) {
        pushNotificationService.pollDeviceToken()
      }
    }

    window.addEventListener('pagehide', handlePageHide)
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      window.removeEventListener('pagehide', handlePageHide)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [auth?.isAuthenticated, currentUser])

  useEffect(() => {
    if (!auth?.isAuthenticated || !state.isRegistered) {
      if (auth?.isAuthenticated && !state.isRegistered) {
        tokenLogger.warn('🔔 Token poll loop NOT started — authenticated but isRegistered=false (no heartbeat)', {
          permission: getNotificationPermission(),
          visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        })
      }
      return
    }

    pushNotificationService.pollDeviceToken()

    const pollInterval = setInterval(() => {
      pushNotificationService.pollDeviceToken()
    }, 2 * 60 * 1000)

    return () => clearInterval(pollInterval)
  }, [auth?.isAuthenticated, state.isRegistered])

  return {
    ...state,
    registerDevice,
    unregisterDevice,
    refreshRegistration,
    requestPermission,
    checkPermission,
    canRequestPermission: () => getNotificationPermission() === 'default',
    isPermissionGranted: () => getNotificationPermission() === 'granted',
    isPermissionDenied: () => getNotificationPermission() === 'denied',
  }
}
